import { type ObjectKind } from "@digits-graphql/frontend/graphql-bearer"
import { type IntervalFromRouteOptions } from "@digits-shared/helpers/dateTimeHelper"
import objectHelper from "@digits-shared/helpers/objectHelper"

export const MODULE_FROM_ROUTE_REG_EXP = /[^/](.[^/]+)/g
const TIME_TOKENS = "/:year/:interval/:index"
const INTENT_ID_QUERY_PARAM = "iid"
const PARAM_PREFIX_REGEX = /^@?:/
const GROUP_PARAM_PREFIX_REGEX = /:[^(]+\(/

export enum DetailsViewAction {
  Push = "Push",
  Replace = "Replace",
  /** Mark a DetailsView as "protected" to prevent it from appearing in the details view "back" button */
  Protected = "Protected",
}

export interface SharedObjectInfo {
  kind: ObjectKind
  id: string
  legalEntitySlug: string
}

/**
 * Returns the human-readable label for the route's module. The return value MUST be plain text;
 * BackButton and similar consumers render it verbatim, so any HTML / markup in the return value
 * would either render escaped or — if a future consumer adopts dangerouslySetInnerHTML — open an
 * XSS vector.
 */
type RouteConfigModuleName = (params: Record<string, string>) => string

export type RouteParams = Record<string, string | number | boolean | undefined | null>

/**
 * Config interface for a route
 */
export interface RouteConfig {
  parameterizedPath: string
  parentRoute?: StaticRouteConfig
  overrideParentParameterizedPath?: boolean
  timeParameterOptions?: IntervalFromRouteOptions
  detailsViewAction?: DetailsViewAction
  detailsStackGroup?: string
  getSharedObjectInfo?: (routeParams: Record<string, string>) => SharedObjectInfo | undefined
  moduleName?: string | RouteConfigModuleName
}

type StaticRouteConfig = RouteConfig | string

/**
 * Interface for loaded routes
 */
export type Routes<StaticRoutesClass extends StaticRoutes> = StaticRoutes & {
  [key in keyof StaticRoutesClass]: DigitsRoute
}

/**
 * Used as the parent class for all static route classes any webapp. Contains shared methods for
 * all static route objects to get.
 */
export class StaticRoutes {
  /**
   * Get all paths of routes that have time tokens included in their paths.
   */
  timeTokenPaths(): string[] {
    return Object.values(this).reduce((timeTokenPaths: string[], route: DigitsRoute) => {
      if (route.config.timeParameterOptions) timeTokenPaths.push(route.parameterizedPath)
      return timeTokenPaths
    }, [])
  }

  /**
   * Get all paths of routes that have object sharing.
   */
  objectSharingPaths(): string[] {
    return Object.values(this).reduce((objectSharingPaths: string[], route: DigitsRoute) => {
      if (route.sharedObjectInfo()) {
        objectSharingPaths.push(route.parameterizedPath.replace(TIME_TOKENS, ""))
      }
      return objectSharingPaths
    }, [])
  }

  pathIsDetailsView(locationName: string): boolean {
    // oxlint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-expect-error
    return !!this[locationName]?.config.detailsViewAction
  }
}

/**
 * Interface for the StaticRoutes constructor object.
 */
interface StaticRoutesConstructor<T extends StaticRoutes> {
  new (): T
}

/**
 * Takes a class of route properties and creates an object with keys of the route name and
 * values of a {@link DigitsRoute} class.
 */
export function createRoutes<SRC extends StaticRoutes>(
  StaticRoutesClass: StaticRoutesConstructor<SRC>
): Routes<SRC> {
  const staticRouteConfig = new StaticRoutesClass() as Routes<SRC>
  const staticRoutes = new StaticRoutesClass() as Routes<SRC>

  objectHelper.keysOf(staticRoutes).forEach((key) => {
    // oxlint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-expect-error
    staticRoutes[key] = new DigitsRoute(
      key.toString(),
      staticRouteConfig[key],
      staticRouteConfig,
      staticRoutes
    )
  })

  return staticRoutes
}

type DigitsRouteConfig = Omit<RouteConfig, "parentRoute"> & { parentRoute?: DigitsRoute }

/**
 * Wraps a provided route that may be parameterized with convenience methods
 * for returning the url with or without the parameters replaced.
 */
export class DigitsRoute {
  readonly name: string
  readonly parameterizedPath: string
  readonly config: DigitsRouteConfig
  readonly moduleName?: RouteConfigModuleName

  constructor(
    name: string,
    config: string | RouteConfig,
    staticRouteConfigs?: Routes<StaticRoutes>,
    constructedRoutes?: Routes<StaticRoutes>
  ) {
    if (typeof config === "string") {
      this.config = { parameterizedPath: config }
      this.parameterizedPath = config
    } else {
      const parentKey = parentRouteKey(name, staticRouteConfigs)
      const parentRoute = parentKey ? (constructedRoutes?.[parentKey] as DigitsRoute) : undefined
      this.config = assembleConfig(name, config, parentRoute)
      this.parameterizedPath = assemblePath(this.config, parentRoute)
      this.config.parameterizedPath = this.parameterizedPath
    }

    if (!this.config.moduleName) {
      let fallbackModuleName = this.config.parameterizedPath
      const fallbackModuleNames = this.config.parameterizedPath.match(MODULE_FROM_ROUTE_REG_EXP)
      if (fallbackModuleNames?.length) {
        fallbackModuleName = fallbackModuleNames[1] ?? fallbackModuleNames[0]
      }
      this.config.moduleName = fallbackModuleName
    }

    this.name = name
    const { moduleName } = this.config
    this.moduleName = typeof moduleName === "string" ? () => moduleName : moduleName

    if (this.config.timeParameterOptions) {
      this.parameterizedPath = `${this.parameterizedPath}${TIME_TOKENS}`
    }
  }

  /**
   * Generate a parameters object that takes the tokenized portions of a route and finds their
   * values in a provided pathname.
   * e.g. route /foo/:bar/:baz, path => /foo/forks/spoons => { bar: "forks", baz: "spoons" }
   */
  getParametersFromPath(pathname: string): Record<string, string> {
    const parameterValues: Record<string, string> = {}

    const atTokenPrefix = /^@:/
    const routeParts = this.parameterizedPath.split("/")
    const pathParts = pathname.split("/")

    for (let i = 1; i < routeParts.length && i < pathParts.length; i += 1) {
      const routePart = routeParts[i] || ""
      const pathPart = pathParts[i] || ""
      const hasAtToken = atTokenPrefix.test(routePart)
      const validAtTokenValue = hasAtToken && /^@/.test(pathPart)

      if (GROUP_PARAM_PREFIX_REGEX.test(routePart)) {
        const routeParam = routePart.replace(/:([^(]+).*/, (match, p1) => p1)
        if (!parameterValues[routeParam]) {
          parameterValues[routeParam] = pathPart
        }
      } else if (PARAM_PREFIX_REGEX.test(routePart) && (!hasAtToken || validAtTokenValue)) {
        const routeParam = routePart.replace(/^@?:/, "")
        parameterValues[routeParam] = pathPart.replace(/^@/, "")
      }
    }

    return parameterValues
  }

  // Used for sharing features and test mocking
  currentPath = () => window.location.pathname

  // Used for sharing features and test mocking
  currentQueryParams = () => new URLSearchParams(window.location.search.slice(1))

  toString() {
    return this.parameterizedPath.toString()
  }

  isRoute(route: DigitsRoute | undefined): boolean {
    if (!route) {
      console.error("No route passed to isRoute of route:", this.name)
      return false
    }

    return route.name === this.name
  }

  isRouteOrChildOfRoute(route: DigitsRoute | undefined): boolean {
    if (!route) {
      console.error("No route passed to isRouteOrChildOfRoute of route:", this.name)
      return false
    }

    return (
      this.isRoute(route) ||
      (route.config.parentRoute ? this.isRouteOrChildOfRoute(route.config.parentRoute) : false)
    )
  }

  sharedObjectInfo() {
    return this.config.getSharedObjectInfo?.(this.getParametersFromPath(this.currentPath()))
  }

  sharingIntentId() {
    return (
      this.sharedObjectInfo() && (this.currentQueryParams().get(INTENT_ID_QUERY_PARAM) || undefined)
    )
  }

  /**
   * Finds the nearest route to this one that does not contain any path parameters. Note: the LE slug does
   * not count as a path parameter for this purpose. This is useful when changing legal entities.
   */
  nearestUnparameterizedRoute(): DigitsRoute | undefined {
    if (!this.config.parameterizedPath.match(/[^@]:/)) {
      return this
    }
    if (!this.config.parentRoute) {
      return undefined
    }
    return this.config.parentRoute.nearestUnparameterizedRoute()
  }
}

const parentRouteKey = (name: string, staticRouteConfigs?: Routes<StaticRoutes>) => {
  if (!staticRouteConfigs) return

  // oxlint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-expect-error
  const parentRouteConfig: StaticRouteConfig | undefined = staticRouteConfigs[name]?.parentRoute

  return objectHelper.keysOf(staticRouteConfigs).find((keyToMatchParent) => {
    const potentialParentConfig: StaticRouteConfig | undefined =
      staticRouteConfigs[keyToMatchParent]

    return potentialParentConfig && parentRouteConfig && potentialParentConfig === parentRouteConfig
  })
}

const assembleConfig = (
  name: string,
  config: RouteConfig,
  parentRoute?: DigitsRoute
): DigitsRouteConfig => {
  const parentConfig = parentRoute?.config || {}
  const { parentRoute: _, ...restConfig } = config
  return {
    ...parentConfig,
    overrideParentParameterizedPath: undefined,
    ...restConfig,
    parentRoute,
  }
}

const assemblePath = (config: RouteConfig, parentRoute?: DigitsRoute): string => {
  if (!config.parentRoute || !parentRoute || config.overrideParentParameterizedPath) {
    return config.parameterizedPath
  }

  return parentRoute.config.parameterizedPath + config.parameterizedPath
}
